"use client";

import { ArrowUpRight, ArrowDown, FileText, MoreHorizontal, ChevronDown } from "lucide-react";
import { siBitcoin, siEthereum, siLitecoin } from "simple-icons/icons";

// --- UI PRIMITIVES ---

export const BrandIcon = ({ icon, className }: { icon: { path: string }, className?: string }) => (
    <svg
        role="img"
        viewBox="0 0 24 24"
        className={className}
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d={icon.path} />
    </svg>
);

export const Icons = {
    BTC: siBitcoin,
    ETH: siEthereum,
    LTC: siLitecoin
};

export const Card = ({ children, className, noPadding }: { children: React.ReactNode; className?: string, noPadding?: boolean }) => (
    <div className={`bg-[#111111] rounded-4xl border border-white/5 overflow-hidden ${className}`}>
        <div className={noPadding ? "" : "p-6"}>{children}</div>
    </div>
);

export const LimeButton = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <button className={`bg-[#bef264] text-black font-semibold px-6 py-3 rounded-full hover:bg-[#a3e635] transition-colors flex items-center gap-2 ${className}`}>
        {children}
    </button>
);

export const PillNav = () => {
    const tabs = ["Overview", "Investments", "Savings", "Income", "Activity"];
    return (
        <div className="flex flex-wrap gap-2">
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    className={`px-6 py-3 rounded-full text-sm font-medium transition-colors ${i === 0
                        ? "bg-[#bef264] text-black"
                        : "bg-[#1c1c1c] text-stone-400 hover:text-white"
                        }`}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
};

// --- SPECIFIC CARDS ---

export const AssetAllocationCard = ({ totalValue, cryptoValue, rwaValue }: { totalValue: number, cryptoValue: number, rwaValue: number }) => {
    // Calculate percentages for visual
    const cryptoShare = totalValue > 0 ? (cryptoValue / totalValue) * 8 : 4; // Out of 8 bubbles
    const rwaShare = 8 - Math.round(cryptoShare);

    return (
        <Card className="h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-medium text-stone-200">Asset Allocation</h3>
                <div className="flex gap-2">
                    <button className="flex items-center gap-1 bg-[#1c1c1c] px-3 py-1.5 rounded-full text-xs font-medium text-stone-400">
                        Weekly <ChevronDown className="w-3 h-3" />
                    </button>
                    <button className="w-8 h-8 rounded-full bg-[#1c1c1c] flex items-center justify-center text-stone-400 hover:text-white">
                        <MoreHorizontal className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="mb-8">
                <div className="text-4xl font-semibold text-white mb-1">${(totalValue / 1000).toFixed(1)}K</div>
                <div className="text-sm text-stone-500">Total</div>
            </div>

            {/* Bubble Chart Simulation - Dynamic based on share */}
            <div className="flex-1 grid grid-cols-4 gap-2 content-end">
                {/* RWA Bubbles (Blue) */}
                {Array.from({ length: rwaShare }).map((_, i) => (
                    <div key={`rwa-${i}`} className="aspect-square rounded-full bg-blue-500/80 border border-white/5" />
                ))}
                {/* Crypto Bubbles (Green) */}
                {Array.from({ length: 8 - rwaShare }).map((_, i) => (
                    <div key={`crypto-${i}`} className={`aspect-square rounded-full ${i === 0 ? 'bg-stone-600' : 'bg-[#bef264]/80'} border border-white/5`} />
                ))}
            </div>

            <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm text-stone-400">
                    <span className="w-2 h-2 rounded-full bg-[#bef264]" /> Crypto Assets (${(cryptoValue / 1000).toFixed(1)}k)
                </div>
                <div className="flex items-center gap-3 text-sm text-stone-400">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> Real World Assets (${(rwaValue / 1000).toFixed(1)}k)
                </div>
            </div>
        </Card>
    )
}

export const PromoCard = () => {
    return (
        <Card className="relative h-full overflow-hidden bg-linear-to-br from-neutral-900 to-black group">
            {/* Topographic Background Pattern */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                    <pattern id="topo" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                        <path d="M0,50 Q25,25 50,50 T100,50" fill="none" stroke="white" strokeWidth="0.5" />
                        <path d="M0,20 Q40,0 60,30 T100,20" fill="none" stroke="white" strokeWidth="0.5" opacity="0.5" />
                        <path d="M0,80 Q30,60 70,90 T100,80" fill="none" stroke="white" strokeWidth="0.5" opacity="0.5" />
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#topo)" />
                </svg>
            </div>

            <div className="relative z-10 flex flex-col justify-between h-full min-h-[220px]">
                <h3 className="text-3xl font-medium leading-tight max-w-sm">
                    Join Our Crypto <br /> Mastery Class
                </h3>

                <div className="flex items-end justify-between">
                    <div className="flex items-center gap-2">
                        <span className="bg-stone-800 text-stone-300 text-xs font-bold px-3 py-1 rounded-full">+15%</span>
                        <span className="text-xs text-stone-500">discount for members</span>
                    </div>
                    <LimeButton className="py-2! px-4! text-sm">
                        Join Class <ArrowUpRight className="w-4 h-4 ml-1" />
                    </LimeButton>
                </div>
            </div>
        </Card>
    );
};

export const StatsCard = ({ earnings, percentage }: { earnings: number, percentage: number }) => {
    return (
        <Card className="h-full flex flex-col justify-between min-h-[200px]">
            <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center mb-4">
                <ArrowDown className="w-5 h-5 text-white" />
            </div>

            <div>
                <div className="text-stone-400 text-sm mb-1">Today's Earnings</div>
                <div className="text-4xl font-semibold mb-2">${earnings.toLocaleString()}</div>
                <div className="text-sm font-medium text-stone-500">
                    <span className="text-white">+{percentage}%</span> Percentage Change
                </div>
            </div>
        </Card>
    )
}

export const ReportCard = () => {
    return (
        <Card className="h-full relative overflow-hidden min-h-[200px] border-0" noPadding>
            {/* Abstract Green Liquid Background */}
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop')] bg-cover bg-center opacity-80 mix-blend-overlay" />
            <div className="absolute inset-0 bg-linear-to-t from-emerald-900/80 to-transparent" />

            <div className="relative z-10 p-6 flex flex-col h-full justify-between">
                <div className="flex justify-between items-start">
                    <button className="w-8 h-8 bg-black/30 backdrop-blur-md rounded-lg flex items-center justify-center text-white border border-white/10">
                        <FileText className="w-4 h-4" />
                    </button>
                    <button className="w-8 h-8 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10">
                        <span className="text-sm">✕</span>
                    </button>
                </div>

                <div>
                    <h3 className="text-xl font-medium mb-2">Portfolio Report</h3>
                    <p className="text-xs text-stone-300 leading-relaxed max-w-[200px]">
                        Generate and download a detailed report of your crypto performance
                    </p>
                </div>
            </div>
        </Card>
    )
}
